import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { PlayCircle, FilePdf, Eye, CheckCircle } from "@phosphor-icons/react";
import { VideoQuiz } from "@/components/VideoQuiz";

const BASE = "/training-videos";
const VIDEO_VERSION = "2026-10-16c";
const COURSE_LABEL = { agent: "Videos de Agentes", supervisor: "Videos de Supervisores", admin: "Videos de Administradores" };
const MANUAL = { agent: ["agente", "Agente"], supervisor: ["supervisor", "Supervisor"], admin: ["admin", "Administrador"] };

const StatusBadge = ({ v }) => {
  if (v.passed) return <Badge className="text-[9px] bg-emerald-500/15 text-emerald-700 border-transparent gap-1"><CheckCircle size={10} weight="fill" /> Aprobado</Badge>;
  if (v.watched_at) return <Badge className="text-[9px] bg-amber-500/15 text-amber-700 border-transparent gap-1"><Eye size={10} /> Visto · falta cuestionario</Badge>;
  return <Badge variant="outline" className="text-[9px]">Pendiente</Badge>;
};

const VideoCard = ({ v, n, onOpen }) => (
  <button onClick={() => onOpen(v)} data-testid={`training-video-${v.id}`}
    className="text-left rounded-xl border border-border bg-card overflow-hidden hover:-translate-y-0.5 transition-transform group">
    <div className="relative aspect-video bg-[#0A1420] flex items-center justify-center">
      <span className="absolute top-2 left-2 text-[10px] font-mono text-white/60">{String(n).padStart(2, "0")}</span>
      <PlayCircle size={44} weight="fill" className="text-amber-400 group-hover:scale-110 transition-transform" />
    </div>
    <div className="p-3">
      <div className="font-semibold text-sm">{v.title}</div>
      <div className="text-xs text-muted-foreground mt-0.5">{v.desc}</div>
      <div className="mt-2" data-testid={`training-video-status-${v.id}`}><StatusBadge v={v} /></div>
    </div>
  </button>
);

export const TrainingVideos = () => {
  const [videos, setVideos] = useState([]);
  const [open, setOpen] = useState(null);
  const load = () => api.get("/training/videos").then((r) => setVideos(r.data.videos || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const onProgress = (time, duration) => {
    if (!open || open.watched_at || !duration || time / duration < 0.9) return;
    const id = open.id;
    setOpen((o) => ({ ...o, watched_at: new Date().toISOString() }));
    api.post(`/training/videos/${id}/watched`).then(load);
  };

  return (
    <div className="mt-8 space-y-6" data-testid="training-videos">
      {["agent", "supervisor", "admin"].map((course) => {
        const list = videos.filter((v) => v.course === course);
        if (!list.length) return null;
        return (
          <div key={course} data-testid={`training-videos-${course}`}>
            <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
              <div className="text-[11px] uppercase text-muted-foreground font-bold tracking-wider">
                {COURSE_LABEL[course]} · {list.filter((v) => v.passed).length}/{list.length} aprobados
              </div>
              <div className="flex gap-2 flex-wrap">
                <a href={`${BASE}/manual-${MANUAL[course][0]}.pdf`} download data-testid={`training-manual-${course}`}>
                  <Button size="sm" variant="outline" className="gap-1.5 h-8 text-xs">
                    <FilePdf size={14} weight="bold" /> Manual de uso · {MANUAL[course][1]}
                  </Button>
                </a>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {list.map((v, i) => <VideoCard key={v.id} v={v} n={i + 1} onOpen={setOpen} />)}
            </div>
          </div>
        );
      })}
      <Dialog open={!!open} onOpenChange={(o) => !o && setOpen(null)}>
        <DialogContent className="max-w-4xl p-0 overflow-hidden max-h-[92vh] overflow-y-auto" data-testid="training-video-dialog">
          <DialogHeader className="px-5 pt-4"><DialogTitle>{open?.title}</DialogTitle></DialogHeader>
          {open && (
            <>
              <video key={open.id} src={`${BASE}/${open.id}.mp4?v=${VIDEO_VERSION}`} controls autoPlay playsInline className="w-full bg-black"
                onTimeUpdate={(e) => onProgress(e.target.currentTime, e.target.duration)}
                onEnded={(e) => onProgress(e.target.duration, e.target.duration)} data-testid="training-video-player" />
              <VideoQuiz video={open} onPassed={() => { setOpen((o) => ({ ...o, passed: true })); load(); }} />
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
